import type { CSSProperties } from "react";
import { useT } from "../i18n";
import type { SceneDto } from "../../shared/api-types";
import {
  HOTSPOT_SIZE_SCALE,
  hotspotMarkerHtml,
  type HotspotStyle,
} from "../../shared/hotspot-style";

/** Wo die Beispiel-Marker auf der Platte sitzen (% der Fläche). */
const SPOTS = [
  { left: 30, top: 60 },
  { left: 71, top: 47 },
];

/**
 * Beispielansicht der Hotspots: die eingestellte Gestaltung auf dem eigenen
 * Panorama, live beim Umstellen. Marker-HTML und CSS sind dieselben wie im
 * Viewer (shared/hotspot-style.ts, shared/hotspot.css) — was hier steht, steht
 * später auch in der Tour.
 *
 * Nur zum Ansehen: die Marker nehmen keine Maus an, damit kein Zeigefinger
 * Klickbarkeit verspricht, die es hier nicht gibt.
 */
export function HotspotPreview({ style, scenes }: { style: HotspotStyle; scenes: SceneDto[] }) {
  const t = useT();
  const ready = scenes.filter((s) => s.status === "ready" && s.previewUrl);
  // Beschriftungen gekürzt: in der Tour hat ein langer Szenenname Platz, auf
  // der kleinen Platte liefe er sonst über den Rand hinaus.
  const labels = ready
    .slice(0, SPOTS.length)
    .map((s) => (s.name.length > 24 ? `${s.name.slice(0, 23).trimEnd()}…` : s.name));

  return (
    <div
      className={`relative isolate aspect-[16/7] w-full overflow-hidden rounded-xl bg-zinc-800 ring-1 ring-black/10 dark:ring-white/10 ${
        style.pulse ? "" : "f360-hs-nopulse"
      }`}
      style={
        {
          "--f360-hs-color": style.color,
          "--f360-hs-scale": String(HOTSPOT_SIZE_SCALE[style.size]),
        } as CSSProperties
      }
    >
      {ready[0]?.previewUrl ? (
        // Der Ausschnitt ist bewusst herangezoomt: das ganze Äquirektangular-Bild
        // auf einen Streifen gequetscht sähe der Tour nicht ähnlich.
        <img
          src={ready[0].previewUrl}
          alt=""
          className="absolute inset-0 h-full w-full scale-[1.7] object-cover"
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-600 via-zinc-700 to-zinc-900" />
      )}

      {SPOTS.map((spot, i) => (
        <div
          key={i}
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${spot.left}%`, top: `${spot.top}%` }}
          // Escaping steckt in hotspotMarkerHtml — Szenennamen kommen von Nutzern.
          dangerouslySetInnerHTML={{
            __html: hotspotMarkerHtml(style, labels[i] ?? labels[0] ?? t.tour.hotspotPreviewSample)
              .html,
          }}
        />
      ))}

      <span className="absolute left-2 top-2 rounded-md bg-black/45 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
        {t.tour.hotspotPreview}
      </span>
    </div>
  );
}
